/**
 * Multi-Agent Architecture Types
 *
 * Defines interfaces for the hierarchical agent system:
 * - Main Agent: ONE LLM.streamWithTools() call with source agent tools
 * - Source Agents: independent agents that query individual data sources
 *
 * The main agent sees only source summaries. When it calls a source tool,
 * the SourceAgent runs independently (own LLM, own retries) and returns clean data.
 */

import type { ExecutedToolInfo } from '../services/tool-executor-service';
import { MAX_ROWS_FETCHED } from '../constants';

// ============================================
// Source Summary (for routing decisions)
// ============================================

/**
 * Per-entity detail: name, row count, and column names.
 * Gives the main agent enough context to route to the right source.
 */
export interface EntityDetail {
	/** Entity name (table, sheet, endpoint) */
	name: string;
	/** Approximate row count */
	rowCount?: number;
	/** Column/field names */
	columns: string[];
}

/**
 * Representation of a data source for the main agent.
 * Contains entity names WITH column names so the LLM can route accurately.
 */
export interface SourceSummary {
	/** Source ID (matches tool ID prefix) */
	id: string;
	/** Human-readable source name */
	name: string;
	/** Source type: postgres, excel, rest_api, etc. */
	type: string;
	/** Brief description of what data this source contains */
	description: string;
	/** Detailed entity info with column names for routing */
	entityDetails: EntityDetail[];
	/** The tool ID associated with this source */
	toolId: string;
}

// ============================================
// Source Tool Input (what the main agent passes to a source tool)
// ============================================

/**
 * Input the main agent sends when calling a source tool.
 * This is what the source agent receives to do its work.
 */
export interface SourceToolInput {
	/** What data to fetch — natural language description */
	intent: string;
	/** How to return data */
	aggregation?: 'raw' | 'pre-aggregate' | 'summary';
}

// ============================================
// Source Agent Result
// ============================================

/**
 * What a source agent returns after querying its data source.
 * The main agent uses this to analyze and compose the final response.
 */
export interface SourceAgentResult {
	/** Source ID */
	sourceId: string;
	/** Source name */
	sourceName: string;
	/** Whether the query succeeded */
	success: boolean;
	/** Result data rows */
	data: any[];
	/** Metadata about the query execution */
	metadata: SourceAgentMetadata;
	/** Tool execution info for the last successful query (backward compat) */
	executedTool: ExecutedToolInfo;
	/** All successful tool executions (primary + follow-up queries) */
	allExecutedTools?: ExecutedToolInfo[];
	/** Error message if failed */
	error?: string;
}

export interface SourceAgentMetadata {
	/** Total rows that matched the query (before limit) */
	totalRowsMatched: number;
	/** Rows actually returned (after limit) */
	rowsReturned: number;
	/** Whether the result was truncated by the row limit */
	isLimited: boolean;
	/** The query/params that were executed */
	queryExecuted?: string;
	/** Execution time in milliseconds */
	executionTimeMs: number;
}

// ============================================
// Workflow Components
// ============================================

/**
 * A pre-built, multi-step UI flow registered with the SDK.
 *
 * When the main agent decides a user's question matches a workflow's whenToUse
 * trigger, it picks the workflow instead of running source agents / generating
 * dashboard components. The LLM extracts the workflow's required props from the
 * prompt (using `propsSchema` as the tool input_schema) and the SDK returns the
 * workflow component directly — no analysis text, no chart generation. The
 * frontend renders the registered workflow component with the LLM-extracted
 * props.
 */
export interface WorkflowDescriptor {
	/** Unique workflow id (used as the LLM tool name) */
	id: string;
	/** Component name on the frontend (matches the registered React component) */
	name: string;
	/** Short human-readable description of what this workflow does */
	description: string;
	/**
	 * 1–2 sentence trigger condition. The LLM uses this to decide if the
	 * user's prompt matches this workflow. Be specific — e.g.
	 * "User wants to *initiate* an inventory transfer (review + submit POs),
	 * not just see analysis or charts."
	 */
	whenToUse: string;
	/**
	 * JSON-schema-style description of the props the workflow needs. Becomes
	 * the LLM tool's input_schema, so the model fills these from the prompt.
	 * Use the same shape as `params` on direct tools — string descriptors with
	 * an optional "(optional)" suffix.
	 *
	 * Example:
	 * ```
	 * {
	 *   selectedStore: 'object — { id, name } of the source branch',
	 *   minROI: 'number (optional) — only show transfers with ROI ≥ this',
	 * }
	 * ```
	 */
	propsSchema: Record<string, string>;
	/**
	 * Optional: static prop defaults merged with LLM-extracted props before
	 * the component is returned. Useful for things like the embedded
	 * `externalTool` config that the workflow uses to fetch its own data.
	 */
	defaultProps?: Record<string, any>;
}

/**
 * The workflow selection captured during a routing call.
 * Set on AgentResponse when the LLM picks a workflow tool.
 */
export interface SelectedWorkflow {
	/** Component name (matches WorkflowDescriptor.name) */
	name: string;
	/** Props extracted from the prompt + merged with workflow.defaultProps */
	props: Record<string, any>;
}

// ============================================
// Agent Response (Final output)
// ============================================

/**
 * The complete response from the multi-agent system.
 * Contains everything needed for text display + component generation.
 */
export interface AgentResponse {
	/** Generated text response (analysis of the data) */
	text: string;
	/** All executed tools across all source agents (for component generation) */
	executedTools: ExecutedToolInfo[];
	/** Individual results from each source agent */
	sourceResults: SourceAgentResult[];
	/**
	 * Populated when MainAgent wrote AND successfully executed a script during its turn.
	 * Caller (agent-user-response.ts) persists it via ScriptStore.save().
	 * Absent when MainAgent didn't write one (trivial question / all attempts failed).
	 */
	savedScript?: AgentWrittenScript;
	/**
	 * Set when the LLM routed the question to a registered workflow component.
	 * When present, the upstream caller should skip component generation and
	 * return this workflow as the response.
	 */
	workflow?: SelectedWorkflow;
}

/**
 * A script MainAgent authored + verified during its turn. Shape aligns with
 * what ScriptStore.save() needs — minus store-assigned fields (id, timestamps, counts).
 */
export interface AgentWrittenScript {
	/**
	 * `ScriptRecipe.id` of the draft that was authored + verified during this turn.
	 * The caller passes this to `ScriptStore.promoteToVerified(recipeId, …)` to
	 * flip the draft to verified status and (when possible) drop the turn-suffix
	 * from its filename.
	 */
	recipeId: string;
	name: string;
	intentDescription: string;
	tags: string[];
	parameters: Array<{
		name: string;
		type: 'string' | 'number' | 'date' | 'date_range' | 'enum' | 'boolean';
		required: boolean;
		default?: any;
		enumValues?: Record<string, string>;
		description: string;
	}>;
	scriptBody: string;
	/** Source IDs referenced by the script (extracted from ctx.query calls) */
	sourceIds: string[];
	/** Tables referenced in the script's SQL (regex-extracted) */
	tables: string[];
	/** Executed queries from the verified run — fed to component generation */
	executedQueries: Array<{
		sourceId: string;
		sourceName: string;
		sql: string;
		data: any[];
		count: number;
		totalCount?: number;
		executionTimeMs: number;
		/**
		 * True for synthetic entries (ctx.emit datasets, the computed:_final
		 * post-JS data). The component generator routes virtual sources through
		 * the script_dataset sentinel toolId so the frontend resolves them via
		 * queryCache instead of attempting to re-execute SQL.
		 */
		virtual?: boolean;
	}>;
}

// ============================================
// Agent Configuration
// ============================================

/**
 * Configuration for the multi-agent system.
 * Controls limits, models, and behavior.
 */
export interface AgentConfig {
	/** Max rows shown to the UI preview / inlined per source (default: 10) */
	maxRowsPerSource: number;
	/**
	 * Max rows a source query may FETCH from the DB server-side (default: 2000).
	 * Decoupled from what the main agent is shown: the full result is fetched and
	 * summarized (bounded), but only a small/complete slice enters LLM context.
	 * This lets small lookups (benchmark maps) arrive COMPLETE without letting
	 * large results blow up context.
	 */
	maxRowsFetched: number;
	/** Model for the main agent (routing + analysis in one LLM call) */
	mainAgentModel: string;
	/** Model for source agent query generation */
	sourceAgentModel: string;
	/** API key for LLM calls */
	apiKey?: string;
	/** Max retry attempts per source agent */
	maxRetries: number;
	/** Max tool calling iterations for the main agent loop */
	maxIterations: number;
	/** Global knowledge base context (static, same for all users/questions — cached in system prompt) */
	globalKnowledgeBase?: string;
	/** Per-request knowledge base context (user-specific + query-matched — dynamic, not cached) */
	knowledgeBaseContext?: string;
	/** Collections registry (ChromaDB search hooks) for embedding-based schema + source search */
	collections?: any;
	/** Optional project ID for scoping embedding searches */
	projectId?: string;
}

/**
 * Default agent configuration
 */
export const DEFAULT_AGENT_CONFIG: AgentConfig = {
	maxRowsPerSource: 10,
	maxRowsFetched: MAX_ROWS_FETCHED,
	mainAgentModel: '',     // will use the provider's default model
	sourceAgentModel: '',   // will use the provider's default model
	maxRetries: 2,          // 2 retries = 3 total query attempts (1 initial + 2 retries for SQL errors)
	maxIterations: 12,      // schema search (2-3) + query attempts (2) + write_script/execute_script (2-3) + LLM responses + final
};
